import React, { useCallback, useMemo } from 'react'
import { AsyncSelect, Field } from '@workwhile/ui'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import { useCompanyDetailsByIdQuery } from 'queries/company'

type PositionOption = { label: string; value: number }

const fuzzyOptions: FullOptions<PositionOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

type PositionPickerProps = {
  value?: number | null
  onChange: (value: number | undefined) => void
  companyId?: number | null
  error?: string
  label?: React.ReactNode
}

export const PositionPicker = ({
  value,
  onChange,
  companyId,
  error,
  label = 'Position',
}: PositionPickerProps) => {
  const { data: companyDetail } = useCompanyDetailsByIdQuery(companyId)

  const positionsOptions = useMemo(() => {
    return (
      companyDetail?.positions
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.map((position) => ({
          label: `${position.name} (${position.id})`,
          value: position.id,
        })) ?? []
    )
  }, [companyDetail])

  const searcher = useMemo(() => {
    return new Searcher<PositionOption, FullOptions<PositionOption>>(
      positionsOptions,
      fuzzyOptions
    )
  }, [positionsOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<PositionOption>[]

      return new Promise((resolve) => {
        resolve(results)
      })
    },
    [searcher]
  )

  return (
    <Field label={label} error={error}>
      <AsyncSelect
        block
        isClearable
        isSearchable
        isDisabled={!companyId}
        placeholder={companyId ? 'Select a position' : 'Select a company first'}
        defaultOptions={positionsOptions}
        loadOptions={loadOptions}
        menuPortalTarget={document.body}
        value={
          positionsOptions.find((option) => option.value === value) || null
        }
        onChange={(option) => {
          onChange(option?.value ?? undefined)
        }}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 10011,
            pointerEvents: 'auto',
          }),
        }}
      />
    </Field>
  )
}
